/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';

import { type CommonProps } from '../../_common/types';

type SuggestionProps = {
  /** The content of the suggestion */
  children?: React.ReactNode;
  /** The visual to display before the content. Should be an icon. */
  leadingVisual?: React.ReactNode;
  /** Whether the suggestion is primary. Renders an animated border. */
  isPrimary?: boolean;
  /** Callback function triggered when the suggestion is clicked */
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** The size of the suggestion */
  size?: 'small' | 'large';
};

/**
 * The component is used to display a suggestion in a chat, or in a prompt.
 * It can be primary, which animates its border.
 *
 * @alpha - Changes to this component may be breaking.
 */
const Suggestion = ({
  children,
  leadingVisual,
  isPrimary,
  onClick,
  size = 'small',
  ref,
  className,
  style,
  htmlAttributes,
  ...restProps
}: CommonProps<'button', SuggestionProps>) => {
  const classes = classNames('ndl-ai-suggestion', className, {
    'ndl-ai-suggestion-special': isPrimary,
    'ndl-disabled': htmlAttributes?.disabled,
    'ndl-large': size === 'large',
    'ndl-small': size === 'small',
  });

  return (
    <button
      type="button"
      ref={ref}
      className={classes}
      style={style}
      onClick={onClick}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="ndl-ai-suggestion-inner">
        {Boolean(leadingVisual) && (
          <div className="ndl-ai-suggestion-leading">{leadingVisual}</div>
        )}
        {children}
      </div>
    </button>
  );
};

export { Suggestion };
